import React, { useState, useEffect, useCallback } from 'react';
import { getPaymentMethods, setDefaultPaymentMethod, deletePaymentMethod } from '../../utils/APIs';
import { useUserContext } from '../../contexts/UserContext';
import Loader from '../../components/UI/Loader/Loader';
import { toast } from 'react-toastify';
import AddCardForm from './AddCardForm';

const PaymentMethods = () => {
    const { userData } = useUserContext();
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddCard, setShowAddCard] = useState(false);

    const fetchData = useCallback(async () => {
        if (!userData?.selectedOrganization?.org_token) return;

        try {
            const [methodsRes] = await Promise.all([
                getPaymentMethods(userData.token, userData?.selectedOrganization?.org_token)
            ]);

            if (methodsRes.status) setPaymentMethods(methodsRes.data);
        } catch (error) {
            console.error("Error fetching billing data:", error);
            toast.error("Failed to load billing information.");
        } finally {
            setLoading(false);
        }
    }, [userData]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleAddCardSuccess = () => {
        setShowAddCard(false);
        fetchData();
    };

    const handleSetDefault = async (methodId) => {
        try {
            const res = await setDefaultPaymentMethod(userData.token, userData?.selectedOrganization?.org_token, methodId);
            if (res.status) {
                toast.success("Default payment method updated.");
                fetchData();
            }
        } catch (error) {
            console.error("Set default error:", error);
            toast.error("Failed to update default payment method.");
        }
    };

    const handleDelete = async (methodId) => {
        if (!window.confirm("Are you sure you want to remove this payment method?")) return;
        try {
            const res = await deletePaymentMethod(userData.token, userData?.selectedOrganization?.org_token, methodId);
            if (res.status) {
                toast.success("Payment method removed.");
                fetchData();
            }
        } catch (error) {
            console.error("Delete error:", error);
            toast.error("Failed to remove payment method.");
        }
    };

    if (loading) return <Loader message="Loading payment methods..." />;

    return (
        <div className="">
            {/* Payment Methods Section */}
            <section className="mb-12">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-800">Payment Methods</h2>
                    {!showAddCard && (
                        <button
                            onClick={() => setShowAddCard(true)}
                            className="text-sm bg-blue-50 text-blue-600 px-4 py-2 rounded-lg font-semibold hover:bg-blue-100 transition-colors flex items-center gap-2 border border-blue-100"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                            </svg>
                            Add Card
                        </button>
                    )}
                </div>

                {showAddCard && (
                    <AddCardForm
                        onSuccess={handleAddCardSuccess}
                        onCancel={() => setShowAddCard(false)}
                    />
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {paymentMethods.length > 0 ? (
                        paymentMethods.map((method, index) => {
                            const isDefault = method.isDefault;
                            return (
                                <div key={method.id} className="border rounded-xl p-5 flex items-center justify-between bg-white shadow-sm hover:shadow-md transition-shadow group relative">
                                    <div className="flex items-center gap-4">
                                        <div className="bg-gray-50 p-3 border rounded-lg">
                                            <svg className="w-10 h-6 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4V12h16v6zm0-10H4V6h16v2z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <p className="font-bold text-gray-900 leading-tight">
                                                    {method.brand.toUpperCase()} •••• {method.last4}
                                                </p>
                                                {isDefault && (
                                                    <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-bold border border-blue-100 uppercase">
                                                        Default
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm text-gray-500 mt-1">
                                                Expires {method.exp_month}/{method.exp_year}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {!isDefault && (
                                            <button
                                                onClick={() => handleSetDefault(method.id)}
                                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                title="Make Default"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleDelete(method.id)}
                                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Remove Card"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="col-span-full border-2 border-dashed border-gray-200 rounded-xl p-10 text-center bg-gray-50">
                            <div className="mx-auto w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm mb-4">
                                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <p className="text-gray-500 font-medium">No payment methods found.</p>
                            <button onClick={() => setShowAddCard(true)} className="text-blue-600 mt-2 font-semibold hover:underline">
                                Add your first card to get started
                            </button>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default PaymentMethods;
